import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Droplet, LineChart, Baby, Sparkles, Heart, Users, User, Settings, CreditCard, LogOut, Calendar, NotebookPen, TrendingUp, Pill, Handshake, Watch, Star, Lock, Dumbbell, LayoutGrid, X } from "lucide-react";
import { useAppAuth } from "./AuthContext";
import { ft } from "./api";
import { usePremiumFonts } from "./PremiumStage";
import { LanguageMenu } from "../components/fc3/LanguageUI";
import { T } from "../lib/fc3i18n";
import "./fc-premium.css";

function readPremium(s) {
  const status = String(s?.status || s?.state || "").toLowerCase();
  const isCancelled = ["cancelled", "canceled", "expired", "past_due", "unpaid", "ended"].includes(status);
  const isPremium = !isCancelled && (s?.is_premium === true || s?.active === true || status === "active" || status === "trialing");
  return { isPremium, isCancelled };
}

function useSubscriptionState() {
  const [state, setState] = useState({ loading: true, isPremium: false, isCancelled: false });
  useEffect(() => {
    let dead = false;
    ft.subscription()
      .then((s) => { if (!dead) setState({ loading: false, ...readPremium(s) }); })
      .catch(() => { if (!dead) setState({ loading: false, isPremium: false, isCancelled: false }); });
    return () => { dead = true; };
  }, []);
  return state;
}

/** Top-right premium button — hidden when the user already has an active plan. */
function GetPremiumButton({ sub }) {
  if (sub.loading || sub.isPremium) return null;
  return (
    <Link to="/app/subscription" data-testid="topbar-get-premium" className="fp-premium-btn">
      <Star fill="currentColor" />
      {sub.isCancelled ? T("Rejoin Premium") : T("Join Premium")}
    </Link>
  );
}

const PREMIUM_ROUTES = new Set(["/app/insights", "/app/trends", "/app/pregnancy", "/app/ai-coach", "/app/medications", "/app/partner", "/app/wearables"]);

const GROUPS = [
  { title: "Today", items: [
    { to: "/app/home", label: "Home", Icon: Home },
    { to: "/app/cycle", label: "Cycle", Icon: Droplet },
    { to: "/app/calendar", label: "Calendar", Icon: Calendar },
    { to: "/app/logs", label: "Daily Log", Icon: NotebookPen },
  ] },
  { title: "Understand", items: [
    { to: "/app/insights", label: "Insights", Icon: LineChart },
    { to: "/app/trends", label: "Trends", Icon: TrendingUp },
    { to: "/app/ai-coach", label: "AI Coach", Icon: Sparkles },
  ] },
  { title: "Life stages", items: [
    { to: "/app/pregnancy", label: "Pregnancy", Icon: Baby },
    { to: "/app/daughter-care", label: "Daughter Care", Icon: Heart },
    { to: "/app/fitness", label: "Fitness", Icon: Dumbbell },
  ] },
  { title: "Care circle", items: [
    { to: "/app/community", label: "Community", Icon: Users },
    { to: "/app/medications", label: "Medications", Icon: Pill },
    { to: "/app/partner", label: "Partner", Icon: Handshake },
    { to: "/app/wearables", label: "Wearables", Icon: Watch },
  ] },
  { title: "Account", items: [
    { to: "/app/subscription", label: "Subscription", Icon: CreditCard },
    { to: "/app/profile", label: "Profile", Icon: User },
    { to: "/app/settings", label: "Settings", Icon: Settings },
  ] },
];
const NAV = GROUPS.flatMap((g) => g.items);
const TABS = ["/app/home", "/app/cycle", "/app/logs", "/app/ai-coach"].map((to) => NAV.find((n) => n.to === to));

export function RequireAuth({ children }) {
  const { user, loading } = useAppAuth();
  if (loading) {
    return (
      <div className="fp-loading" role="status" aria-label={T("Loading")}>
        <div className="fp-loading-mark"><img src="/femcare-logo-v2.png" alt="" /></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/app/login" replace />;
  return children;
}

const slug = (s) => s.toLowerCase().replace(/ /g, "-");

export default function AppShell() {
  usePremiumFonts();
  const { user, logout } = useAppAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const sub = useSubscriptionState();
  const [sheet, setSheet] = useState(false);
  const doLogout = async () => { await logout(); nav("/app/login"); };
  useEffect(() => { setSheet(false); window.scrollTo(0, 0); }, [loc.pathname]);

  const display = user?.name || user?.email?.split("@")[0] || T("Signed in");
  const initials = String(display).split(/\s+/).map((w) => w[0]).join("").slice(0, 2) || "F";
  const current = NAV.find((n) => loc.pathname.startsWith(n.to)) || NAV[0];

  return (
    <div className="fp-shell" data-testid="app-shell">
      <div className="fp-shell-bg" aria-hidden="true" />
      <aside className="fp-side">
        <div className="fp-aurora" aria-hidden="true"><i /><i /><i /></div>
        <div className="fp-side-in">
          <Link to="/app/home" className="fp-side-brand">
            <img src="/femcare-logo-v2.png" alt="Femcare.life" />
            <span>Femcare<b>.life</b></span>
          </Link>
          <nav aria-label={T("App navigation")}>
            {GROUPS.map((g) => (
              <div className="fp-navgroup" key={g.title}>
                <p>{T(g.title)}</p>
                {g.items.map((n) => (
                  <NavLink key={n.to} to={n.to} className={({ isActive }) => `fp-navlink${isActive ? " active" : ""}`}
                    data-testid={`app-nav-${slug(n.label)}`}>
                    <n.Icon aria-hidden="true" />
                    {T(n.label)}
                    {!sub.loading && !sub.isPremium && PREMIUM_ROUTES.has(n.to) && <Lock className="fp-lock" aria-label={T("Premium")} />}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          {!sub.loading && !sub.isPremium && (
            <div className="fp-side-card">
              <strong>{sub.isCancelled ? T("Welcome back") : T("FemCare Premium")}</strong>
              <p>{T("AI insights, wellness reports, Pregnancy Mode and the full Fitness library.")}</p>
              <Link to="/app/subscription"><Star size={13} fill="currentColor" /> {sub.isCancelled ? T("Rejoin Premium") : T("See Premium plans")}</Link>
            </div>
          )}
          <div className="fp-side-user">
            <span className="fp-avatar" aria-hidden="true">{initials}</span>
            <div className="fp-uname">
              <b>{display}</b>
              <span data-testid="app-user-email">{user?.email || (sub.isPremium ? "Premium" : T("Free"))}</span>
            </div>
            <button onClick={doLogout} data-testid="app-logout" className="fp-iconbtn" aria-label={T("Sign out")} title={T("Sign out")}>
              <LogOut />
            </button>
          </div>
        </div>
      </aside>

      <div className="fp-main">
        <header className="fp-top">
          <Link to="/app/home" className="fp-mbrand"><img src="/femcare-logo-v2.png" alt="" /><span>Femcare</span></Link>
          <div className="fp-top-title"><span>Femcare</span><span aria-hidden="true">/</span><b>{T(current.label)}</b></div>
          <div className="fp-top-right">
            <LanguageMenu compact />
            <GetPremiumButton sub={sub} />
          </div>
        </header>
        <main className="fp-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile tab bar + "More" sheet with every section */}
      <nav className="fp-tabbar" aria-label={T("App navigation")}>
        {TABS.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "active" : "")} data-testid={`app-tab-${slug(n.label)}`}>
            <n.Icon aria-hidden="true" />{T(n.label)}
          </NavLink>
        ))}
        <button type="button" onClick={() => setSheet(true)} data-testid="app-tab-more" aria-haspopup="dialog">
          <LayoutGrid aria-hidden="true" />{T("More")}
        </button>
      </nav>
      {sheet && (
        <div className="fp-sheet" role="dialog" aria-modal="true" aria-label={T("All sections")} onClick={() => setSheet(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <div className="fp-grab" />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <p className="fp-serif" style={{ fontSize: 26 }}>{T("All sections")}</p>
              <button className="fp-iconbtn" onClick={() => setSheet(false)} aria-label={T("Close menu")}><X /></button>
            </div>
            <div className="fp-sheet-grid">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "active" : "")}>
                  <n.Icon aria-hidden="true" />{T(n.label)}
                </NavLink>
              ))}
            </div>
            <div className="fp-side-user" style={{ marginTop: 16, padding: "14px 4px 0" }}>
              <span className="fp-avatar" aria-hidden="true">{initials}</span>
              <div className="fp-uname"><b>{display}</b><span>{user?.email || ""}</span></div>
              <button onClick={doLogout} className="fp-iconbtn" aria-label={T("Sign out")}><LogOut /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
